import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import SarapeDivider from "@/components/SarapeDivider";
import { useLang } from "@/context/LanguageContext";
import { useClientAuth } from "@/context/ClientAuthContext";
import { brand } from "@/lib/brand";

export default function Footer() {
  const { t } = useLang();
  const { client } = useClientAuth();
  const [disclaimer, setDisclaimer] = useState("");
  const [brand, setBrand] = useState({ contact_email: "info@holamovers.com", support_email: "info@holamovers.com", whatsapp: "+52 999 361 6898" });

  useEffect(() => {
    api.get("/disclaimers").then((res) => {
      const g = res.data.find((d) => d.key === "global");
      if (g) setDisclaimer(g.text);
    });
    api.get("/brand").then((res) => setBrand(res.data)).catch(() => {});
  }, []);
  const waDigits = (brand.whatsapp || "").replace(/[^0-9]/g, "");

  return (
    <footer className="bg-night text-sand-100 mt-24">
      <SarapeDivider />
      <div className="mx-auto max-w-7xl px-6 lg:px-12 py-16 grid gap-12 md:grid-cols-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-sand-100">
              <img src={brand.logoMark} alt={brand.name} className="h-8 w-auto" />
            </span>
            <span className="font-serif text-xl">{brand.name}</span>
          </div>
          <p className="mt-4 text-sm text-sand-100/70 leading-relaxed max-w-xs">
            {t("footer.blurb")}
          </p>
          <ul className="mt-6 space-y-2 text-sm text-sand-100/80">
            <li>
              <span className="block text-xs text-sand-100/50">WhatsApp</span>
              <a data-testid="footer-whatsapp" href={`https://wa.me/${waDigits}?text=Hola!%20I%20have%20a%20question%20about%20moving%20to%20Mexico.`} target="_blank" rel="noopener noreferrer" className="hover:text-terracotta transition-colors">{brand.whatsapp}</a>
            </li>
            <li>
              <span className="block text-xs text-sand-100/50 mt-2">{t("footer.emailGeneral")}</span>
              <a data-testid="footer-email-info" href={`mailto:${brand.contact_email}?subject=HolaMovers%20Inquiry`} className="hover:text-terracotta transition-colors">{brand.contact_email}</a>
            </li>
            <li>
              <span className="block text-xs text-sand-100/50 mt-2">{t("footer.emailSupport")}</span>
              <a data-testid="footer-email-support" href={`mailto:${brand.support_email}?subject=HolaMovers%20Support%20Request`} className="hover:text-terracotta transition-colors">{brand.support_email}</a>
            </li>
          </ul>
        </div>
        <div>
          <h4 className="text-sm uppercase tracking-[0.2em] text-clay mb-4">{t("footer.company")}</h4>
          <ul className="space-y-2 text-sm text-sand-100/80">
            <li><Link data-testid="footer-login" to={client ? "/portal" : "/account"} className="hover:text-terracotta transition-colors">{client ? t("common.myPortal") : t("common.clientSignIn")}</Link></li>
            <li><Link data-testid="footer-contact" to="/contact" className="hover:text-terracotta transition-colors">{t("footer.contactUs")}</Link></li>
            <li><Link data-testid="footer-about" to="/about" className="hover:text-terracotta transition-colors">{t("footer.about")}</Link></li>
            <li><Link to="/book" className="hover:text-terracotta transition-colors">{t("footer.bookCall")}</Link></li>
            <li><Link data-testid="footer-privacy" to="/privacy" className="hover:text-terracotta transition-colors">{t("footer.privacy")}</Link></li>
            {brand.google_review_link && (
              <li>
                <a data-testid="footer-review-link" href={brand.google_review_link} target="_blank" rel="noopener noreferrer" className="hover:text-terracotta transition-colors">
                  Leave us a Google review ★
                </a>
              </li>
            )}
            {brand.yelp_page_url && (
              <li><a data-testid="footer-yelp" href={brand.yelp_page_url} target="_blank" rel="noopener noreferrer" className="hover:text-terracotta transition-colors">Find us on Yelp</a></li>
            )}
            <li><Link data-testid="footer-reviews" to="/reviews" className="hover:text-terracotta transition-colors">Reviews</Link></li>
            <li><Link data-testid="footer-references" to="/references" className="hover:text-terracotta transition-colors">References</Link></li>
            <li><Link data-testid="footer-review-policy" to="/review-policy" className="hover:text-terracotta transition-colors">Review Policy</Link></li>
            <li><Link data-testid="footer-photo-policy" to="/photo-policy" className="hover:text-terracotta transition-colors">Photo Policy</Link></li>
            <li className="pt-2"><Link to="/admin" className="hover:text-terracotta transition-colors">Admin</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="text-sm uppercase tracking-[0.2em] text-clay mb-4">{t("footer.explore")}</h4>
          <ul className="space-y-2 text-sm text-sand-100/80">
            <li><Link to="/services" className="hover:text-terracotta transition-colors">{t("footer.services")}</Link></li>
            <li><Link to="/start-here" className="hover:text-terracotta transition-colors">{t("nav.howItWorks")}</Link></li>
            <li><Link to="/cost-calculator" className="hover:text-terracotta transition-colors">{t("footer.costCalculator")}</Link></li>
            <li><Link to="/faq" className="hover:text-terracotta transition-colors">FAQ</Link></li>
            <li><Link to="/services#guides" className="hover:text-terracotta transition-colors">{t("footer.guides")}</Link></li>
            <li><Link to="/pets" className="hover:text-terracotta transition-colors">{t("footer.pets")}</Link></li>
            <li><Link to="/blog" className="hover:text-terracotta transition-colors">{t("footer.blog")}</Link></li>
            <li><Link to="/updates" className="hover:text-terracotta transition-colors">Updates</Link></li>
            <li className="pt-2 text-xs text-sand-100/50">Compare us</li>
            <li><Link to="/vs/mexico-relocation-guide" className="hover:text-terracotta transition-colors">vs Mexico Relocation Guide</Link></li>
            <li><Link to="/vs/focus-on-mexico" className="hover:text-terracotta transition-colors">vs Focus on Mexico</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="mx-auto max-w-7xl px-6 lg:px-12 py-6 text-xs text-sand-100/50 leading-relaxed">
          {disclaimer && <p className="mb-3 max-w-3xl">{disclaimer}</p>}
          <p className="mb-2 text-sand-100/70">Based in Mérida, Yucatán · Made with care on both sides of the border · Hecho con cariño a ambos lados de la frontera.</p>
          <p>© {new Date().getFullYear()} {brand.name}. A planning &amp; support service — not a legal or immigration provider.</p>
        </div>
      </div>
    </footer>
  );
}
